import { Avatar, Group, Image, Pill, Text } from '@mantine/core'
import { IconPhotoScan } from '@tabler/icons-react'
import { useMemo } from 'react'

import avatars from '@/assets/avatars'
import { CollapsibleContentSection } from '@/components/CollapsibleContentSection'
import { EmptyState } from '@/components/EmptyState'
import { TableContentGrid } from '@/components/TableContentGrid'
import { useGlobalState } from '@/hooks/useGlobalState'
import { getDefaultPskAvatar } from '@/screens/SetupScreen/utils'

import { useContentModuleScreenData } from './ContentModuleScreen'

export function ModuleSection() {
  const { configStore } = useGlobalState()
  const data = useContentModuleScreenData()

  const eskName = useMemo(() => {
    // For MSK content modules, always show "Personal Sidekick"
    if (data.cmQuery.data?.visibleToMsk) {
      return 'Personal Sidekick'
    }

    // For ESK content modules, find the ESK by selectEsk
    const selectEsk = data.cmQuery.data?.selectEsk
    if (selectEsk) {
      // Handle PSK case - show "Personal Sidekick" instead of "Avatar PSK"
      if (selectEsk === 'PSK') {
        return 'Personal Sidekick'
      }

      const esk = data.esksQuery.data?.find(e => e.eskId === selectEsk)
      return esk?.name ?? `Avatar ${selectEsk}`
    }

    return 'No Sidekick Selected'
  }, [data.cmQuery.data?.selectEsk, data.cmQuery.data?.visibleToMsk, data.esksQuery.data])

  const eskAvatar = useMemo(() => {
    // For MSK content modules, use the default PSK avatar
    if (data.cmQuery.data?.visibleToMsk) {
      return getDefaultPskAvatar(data.sidekicksQuery.data)
    }

    // For ESK content modules, find the ESK by selectEsk
    const selectEsk = data.cmQuery.data?.selectEsk
    if (!selectEsk || !data.esksQuery.data) return undefined

    const esk = data.esksQuery.data.find(e => e.eskId === selectEsk)
    return esk ? avatars[esk.avatarId] : undefined
  }, [data.cmQuery.data?.selectEsk, data.cmQuery.data?.visibleToMsk, data.esksQuery.data, data.sidekicksQuery.data])

  const rows = useMemo(() => {
    const baseRows = [
      { heading: 'Title', content: <Text fw={600}>{data.cmQuery.data?.title}</Text> },
      {
        heading: 'Sidekick',
        content: (
          <Group align="center">
            <Avatar src={eskAvatar} variant={!!eskAvatar ? 'transparent' : 'light'} radius="sm" size="lg" />
            <Text>{eskName}</Text>
          </Group>
        ),
      },
      {
        heading: 'Module Description',
        content: (
          <Text style={{ whiteSpace: 'pre-wrap' }}>{data.cmQuery.data?.shortDescription}</Text>
        ),
      },
      { heading: 'Topic', content: data.cmQuery.data?.categories?.join(', ') },
      {
        heading: 'Sub-Topics',
        content: (
          <Group>
            {data.cmQuery.data?.mappings?.map(m => (
              <Pill key={m} size="lg">
                {m}
              </Pill>
            ))}
          </Group>
        ),
      },
      // {
      //   heading: 'Module Flow',
      //   content: (
      //     <RadioContentCell
      //       items={[
      //         { label: 'User Directed', selected: !data.cmQuery.data?.areStepSequential },
      //         { label: 'Sequential', selected: !!data.cmQuery.data?.areStepSequential },
      //       ]}
      //     />
      //   ),
      // },
      //{ heading: 'Expiration Date', content: <ComingSoon /> },
      //{ heading: 'Due Date', content: <ComingSoon /> },
      {
        heading: 'Target Audience',
        content: (
          <Text style={{ whiteSpace: 'pre-wrap' }}>{data.cmQuery.data?.demographic || 'Not specified'}</Text>
        ),
      },
      {
        heading: 'Module Introduction',
        content: (
          <Text style={{ whiteSpace: 'pre-wrap' }}>{data.cmQuery.data?.introduction || 'Not specified'}</Text>
        ),
      },
      {
        heading: 'Module Intro Image',
        content: data.cmQuery.data?.titleImage ? (
          <Image
            fit="contain"
            radius="md"
            h="auto"
            mah="300px"
            w="auto"
            maw="100%"
            src={`${configStore.serverBaseUrl}/files/${data.cmQuery.data?.titleImage}`}
          />
        ) : (
          <EmptyState size="sm" Icon={IconPhotoScan} title="No Intro Image Uploaded" />
        ),
      },
    ]

    return baseRows
  }, [data.cmQuery.data, eskName, eskAvatar, configStore.serverBaseUrl])

  return (
    <CollapsibleContentSection
      id="module-details"
      title="Module Details"
      subtitle="General module information and user experience settings."
      ContentElement={<TableContentGrid headingFlexWidth="245px" rows={rows} />}
    />
  )
}
